import { useCallback, useMemo } from 'react';

import type { ApolloLink, Reference } from '@apollo/client';
import { Apollo } from 'src/gql/generated';
import { stepTypename, workflowTypename } from 'src/helpers';
import { useGetWorkflowWithSteps } from 'src/hooks';
import { getSigningStepsFromWorkflow } from 'src/workflow';

export type ActionType = 'field' | 'cc';

/** Returns function that can create an action. */
export function useCreateActionType() {
  const [createWorkflowRecipient, { loading: creatingRecipient }] =
    Apollo.useCreateWorkflowRecipientMutation();

  return {
    createAction: useCallback(
      async ({
        actionPlacement,
        actionType,
        assignedRoleId,
        assignedUserId,
        email,
        isLauncher,
        recipientName,
        targetStepId,
        workflowId,
      }: {
        actionPlacement: Apollo.ActionPlacement;
        actionType: ActionType;
        assignedRoleId: UUID | null;
        assignedUserId: UUID | null;
        email: string | null;
        targetStepId: UUID | null;
        workflowId: UUID;
        isLauncher?: boolean;
        recipientName?: string | null;
      }) => {
        switch (actionType) {
          case 'cc':
          case 'field': {
            await createWorkflowRecipient({
              variables: {
                input: {
                  actionPlacement,
                  email,
                  props: {
                    assignedRoleId,
                    assignedUserId,
                    isLauncher: isLauncher ?? false,
                    type:
                      actionType === 'field'
                        ? Apollo.WorkflowRecipientType.Signer
                        : Apollo.WorkflowRecipientType.CarbonCopy,
                    workflowId,
                  },
                  recipientName: recipientName ?? null,
                  targetStepId,
                },
              },
            });
            break;
          }
        }
      },
      [createWorkflowRecipient]
    ),
    loading: creatingRecipient,
  };
}

export interface Assignee {
  assignedRoleId: UUID | null;
  assignedUserId: UUID | null;
  isLauncher?: boolean;
}

const allActionTypes: ActionType[] = ['field', 'cc'];

/**
 * Returns the action types that are allowed for the given `assignee`
 * in a given `section` of a `workflow`.
 */
export function useAllowedActionTypes({
  assignee,
  workflowId,
}: {
  assignee: Assignee;
  workflowId: UUID;
}) {
  const { workflow } = useGetWorkflowWithSteps(workflowId);
  const orderedSteps = getSigningStepsFromWorkflow(workflow);
  const allActions = orderedSteps.flatMap(s => s.actions);
  // Ensures that the same recipient is not added multiple times for the same action (e.g., same recipient signs multiple times).
  const { availableActionsForRecipient } = useMemo(() => {
    const excludedActionTypes = new Set(
      allActions
        .filter(a => {
          // The launcher has no user or role, so it matches by its flag alone.
          if (assignee.isLauncher || a.permission.isLauncher)
            return Boolean(assignee.isLauncher && a.permission.isLauncher);

          const assigneeIdOrNull =
            a.permission.account?.__typename === 'User' ? a.permission.account.id : null;
          const assignedRoleIdOrNull = a.permission.role?.id ?? null;
          return (
            assignee.assignedUserId === assigneeIdOrNull &&
            assignee.assignedRoleId === assignedRoleIdOrNull
          );
        })
        .map((assignedAction): ActionType => {
          return assignedAction.type === Apollo.WorkflowRecipientType.Signer ? 'field' : 'cc';
        })
    );
    const availableActionsForRecipient = allActionTypes
      .filter(actionType => {
        const isDuplicate = excludedActionTypes.has(actionType);
        return !isDuplicate;
      })
      .map(actionType => actionType);

    return { availableActionsForRecipient, excludedActionTypes };
  }, [allActions, assignee]);

  return { availableActionsForRecipient };
}

export function useDeleteActions() {
  const [deleteActions, { loading }] = Apollo.useDeleteWorkflowActionsMutation();
  const safeDeleteActions = useCallback(
    (
      workflowId: UUID,
      stepId: UUID,
      options: Parameters<Apollo.DeleteWorkflowActionsMutationHookResult[0]>[0]
    ): Promise<ApolloLink.Result<Apollo.DeleteWorkflowActionsMutation>> => {
      return deleteActions({
        ...options,
        update: (cache, { data }) => {
          if (data) {
            const actionsKey: keyof Apollo.WorkflowStep = 'actions';
            const unusedActionsKey: keyof Apollo.Workflow = 'unusedActions';
            const actionsIds = options?.variables?.input.actionIds ?? [];
            cache.modify({
              fields: {
                [actionsKey]: (refs: readonly Reference[] = [], { readField }) => {
                  return refs.filter((ref: Reference) => {
                    const refId = readField<UUID>('id', ref);
                    return refId && !actionsIds.includes(refId);
                  });
                },
              },
              id: cache.identify({ __typename: stepTypename, id: stepId }),
            });
            cache.modify({
              fields: {
                [unusedActionsKey]: (refs: readonly Reference[] = [], { readField }) => {
                  return refs.filter((ref: Reference) => {
                    const refId = readField<UUID>('id', ref);
                    return refId && !actionsIds.includes(refId);
                  });
                },
              },
              id: cache.identify({ __typename: workflowTypename, id: workflowId }),
            });
          }
        },
      });
    },
    [deleteActions]
  );

  return { deleteActions: safeDeleteActions, loading };
}
